import { faChevronDown, faChevronUp, faFilter, faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Autocomplete, Button, InputAdornment, TextField, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { CAMPAIGN_LANGUAGES, getAllCampaignLanguages } from "campaign/model"
import {
  FILTER_MEDIA_APPROVAL_BODY_HEIGHT_IN_REM,
  FILTER_MEDIA_APPROVAL_EXPANDED_HEIGHT_IN_REM,
  FILTER_MEDIA_APPROVAL_HEADER_HEIGHT_IN_REM,
  ISspMediaApprovalFilters
} from "media/model"
import {
  clearMediaApprobationFilters,
  getMediaApprovalActiveFilterCount,
  setMediaApprobationFilterPanelExpansion,
  setMediaApprovalFilters
} from "media/tools/media-approval"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { TimeFilter } from "ui/components/TimeFilter"
import { getCreationDateFilters } from "ui/model"
import { isPhoneScreen } from "ui/tools/responsive"
import { StatusFilter } from "./StatusFilter"

interface IMediaApprovalListFiltersProps {
  filters: ISspMediaApprovalFilters
  isExpanded: boolean
  isPhone: boolean
}

const MediaApprovalListFiltersFn = React.memo(
  ({ isExpanded, isPhone, filters }: IMediaApprovalListFiltersProps): JSX.Element => {
    const activeFilterCount = getMediaApprovalActiveFilterCount(filters)
    return (
      <FlexBox
        vertical
        fullWidth
        sx={{
          height: isExpanded
            ? `${FILTER_MEDIA_APPROVAL_EXPANDED_HEIGHT_IN_REM}rem`
            : `${FILTER_MEDIA_APPROVAL_HEADER_HEIGHT_IN_REM}rem`,
          border: theme => theme.palette.border,
          mt: "1rem",
          overflow: "hidden",
          borderRadius: ".25rem",
          transition: "all 400ms ease"
        }}>
        <FlexBox
          id="media-approval-filters-header"
          jc="space-between"
          sx={{
            "width": "100%",
            "height": `${FILTER_MEDIA_APPROVAL_HEADER_HEIGHT_IN_REM}rem`,
            "cursor": "pointer",
            "backgroundColor": theme => theme.palette.background.paper,
            "p": ".5rem",
            "pl": "1rem",
            "pr": "1rem",
            "borderTopLeftRadius": ".25rem",
            "borderTopRightRadius": ".25rem",
            "& .filter-icon": {
              color: theme => theme.palette.text.primary,
              mr: ".5rem"
            },
            "& .expansion-icon": {
              color: theme => theme.palette.text.primary,
              mt: ".15rem"
            }
          }}
          onClick={() => setMediaApprobationFilterPanelExpansion(!isExpanded)}>
          <Typography
            sx={{
              "fontSize": "1.1rem",
              "color": theme => theme.palette.text.primary,
              "& .icon": {
                color: theme =>
                  `${activeFilterCount > 0 ? theme.palette.primary.main : theme.palette.text.primary} !important`
              }
            }}>
            <FontAwesomeIcon icon={faFilter} className="filter-icon" />
            {translate("label.filters")}
            {!isPhone && (
              <>
                {" "}
                <Typography
                  id="media-approval-filters-active-count"
                  component="span"
                  sx={{ fontSize: ".9rem" }}
                  color="textSecondary">
                  {`- ${translate("active.count", activeFilterCount)}`}
                </Typography>
              </>
            )}
          </Typography>
          <FlexBox>
            <Button
              id="media-approval-filters-clear-button"
              disabled={activeFilterCount === 0}
              variant="text"
              onClick={event => {
                event.stopPropagation()
                clearMediaApprobationFilters()
              }}
              sx={{ height: "1.8rem", mr: "1rem" }}>
              {translate("label.clear.filters")}
            </Button>
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="expansion-icon" />
          </FlexBox>
        </FlexBox>
        <FlexBox
          id="filter-content"
          jc="space-around"
          wrap
          sx={{
            "width": "100%",
            "height": isExpanded ? `${FILTER_MEDIA_APPROVAL_BODY_HEIGHT_IN_REM}rem` : 0,
            "minHeight": isExpanded ? `${FILTER_MEDIA_APPROVAL_BODY_HEIGHT_IN_REM}rem` : 0,
            "maxHeight": isExpanded ? `${FILTER_MEDIA_APPROVAL_BODY_HEIGHT_IN_REM}rem` : 0,
            "p": ".5rem",
            "pl": "1rem",
            "pr": "1rem",
            "pt": 0,
            "transition": "all 400ms ease",
            "overflow": "auto",
            "& > *": {
              mt: ".5rem !important",
              display: isExpanded ? undefined : "none"
            },
            "& .MuiAutocomplete-tag": {
              maxWidth: "12rem !important"
            }
          }}>
          <TextField
            id="media-approval-filter-name"
            label={translate("label.name")}
            size="small"
            value={filters.name}
            onChange={event => setMediaApprovalFilters({ name: event.target.value })}
            InputProps={{
              endAdornment: filters.name && (
                <InputAdornment
                  position="end"
                  sx={{
                    "& .icon": {
                      cursor: "pointer"
                    }
                  }}>
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="icon"
                    onClick={() => setMediaApprovalFilters({ name: "" })}
                  />
                </InputAdornment>
              )
            }}
          />
          <StatusFilter
            id="media-approval-filter-status"
            value={filters.approvalStatuses}
            label={translate("label.status")}
            onChange={approvalStatusIds => setMediaApprovalFilters({ approvalStatuses: approvalStatusIds })}
            sx={{ minWidth: "14rem" }}
          />
          <TimeFilter
            id="media-approval-filter-creation"
            filters={getCreationDateFilters()}
            value={filters.createdAt}
            label={translate("label.creationDate")}
            onChange={createdAt => setMediaApprovalFilters({ createdAt })}
            sx={{ minWidth: "14rem", maxWidth: "14rem" }}
          />
          <Autocomplete
            multiple
            id="media-approval-filter-language"
            size="small"
            sx={{ minWidth: "14rem" }}
            options={getAllCampaignLanguages()}
            getOptionLabel={(option: CAMPAIGN_LANGUAGES) => translate(`label.language.${option}`)}
            value={filters.languages}
            filterSelectedOptions
            onChange={(_event: any, newValue) => setMediaApprovalFilters({ languages: newValue })}
            renderInput={params => (
              <TextField {...params} id="media-approval-filter-language-input" label={translate("label.language")} />
            )}
          />
        </FlexBox>
      </FlexBox>
    )
  }
)

export const MediaApprovalListFilters = connect(
  (state: IRootState): IMediaApprovalListFiltersProps => ({
    filters: state.media.sspMediaApproval.filters,
    isExpanded: state.media.sspMediaApproval.isFilterExpanded,
    isPhone: isPhoneScreen(state)
  })
)(MediaApprovalListFiltersFn)
